import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Lock } from "lucide-react";

export const OtherCosts = ({ formData, setFormData, fsiBreakdown, setOtherCostsData }) => {
  const [costBreakdown, setCostBreakdown] = useState(null);

  useEffect(() => {
    calculateOtherCosts();
  }, [
    fsiBreakdown,
    formData.marketingExpenseMode,
    formData.marketingExpensePercent,
    formData.marketingCostPerSqft,
    formData.saleRateFlats,
    formData.saleRateOffices,
    formData.saleRateShops,
    formData.saleRatePerSqft,
    formData.miscExpPerSqft,
    formData.brokerageCommissionType,
    formData.brokerageCommissionFixed,
    formData.brokerageCommissionPerSqft,
    formData.loadingPercentage,
    formData.carpetAreaFactor,
    formData.freeAreaPercentFlats,
    formData.freeAreaPercentOffices,
    formData.freeAreaPercentShops,
    formData.additionalFreeAreaPercent,
    formData.areaTerrace,
    formData.areaOverheadTerrace,
    formData.areaGarden,
    formData.terraceConversionRatio,
    formData.overheadTerraceConversionRatio,
    formData.gardenConversionRatio,
    formData.applyIncrementOnTerraceGarden,
    formData.areaFlats,
    formData.areaOffices,
    formData.areaShops,
    formData.freeAreaToSociety
  ]);

  const calculateOtherCosts = () => {
    if (!fsiBreakdown) {
      setCostBreakdown(null);
      return;
    }

    // Calculate saleable area from FSI breakdown
    const totalFSIArea = fsiBreakdown.totalFSIWithAncillary || 0;
    const carpetFactor = parseFloat(formData.carpetAreaFactor) || 70;
    const loadingPct = parseFloat(formData.loadingPercentage) || 40;
    
    const totalCarpetArea = totalFSIArea * (carpetFactor / 100);
    
    // Get existing area in sq mtr
    const conversionFactor = 0.092903;
    const existingFlats = (parseFloat(formData.areaFlats) || 0) * conversionFactor;
    const existingOffices = (parseFloat(formData.areaOffices) || 0) * conversionFactor;
    const existingShops = (parseFloat(formData.areaShops) || 0) * conversionFactor;
    const existingCarpetArea = existingFlats + existingOffices + existingShops;
    
    // Additional free area (use freeAreaPercentFlats as primary, fallback to additionalFreeAreaPercent)
    const freeAreaPercentFlats = parseFloat(formData.freeAreaPercentFlats) || parseFloat(formData.additionalFreeAreaPercent) || 0;
    const freeAreaPercentOffices = parseFloat(formData.freeAreaPercentOffices) || freeAreaPercentFlats;
    const freeAreaPercentShops = parseFloat(formData.freeAreaPercentShops) || freeAreaPercentFlats;
    const additionalFreeCarpet = existingFlats * (freeAreaPercentFlats / 100) + existingOffices * (freeAreaPercentOffices / 100) + existingShops * (freeAreaPercentShops / 100);
    
    // Terrace/Garden converted area
    const areaTerrace = (parseFloat(formData.areaTerrace) || 0) * conversionFactor;
    const areaOverheadTerrace = (parseFloat(formData.areaOverheadTerrace) || 0) * conversionFactor;
    const areaGarden = (parseFloat(formData.areaGarden) || 0) * conversionFactor;
    const terraceConversion = parseFloat(formData.terraceConversionRatio) || 25;
    const overheadTerraceConversion = parseFloat(formData.overheadTerraceConversionRatio) || 25;
    const gardenConversion = parseFloat(formData.gardenConversionRatio) || 25;
    const convertedTerrace = areaTerrace * (terraceConversion / 100);
    const convertedOverheadTerrace = areaOverheadTerrace * (overheadTerraceConversion / 100);
    const convertedGarden = areaGarden * (gardenConversion / 100);
    const applyIncrementOnTG = formData.applyIncrementOnTerraceGarden === "yes";
    const incrementOnTG = applyIncrementOnTG ? (convertedTerrace + convertedOverheadTerrace + convertedGarden) * (freeAreaPercentFlats / 100) : 0;
    const totalTerraceGarden = convertedTerrace + convertedOverheadTerrace + convertedGarden + incrementOnTG;
    
    // Balance saleable carpet area (matching calculations.js)
    const totalFreeArea = additionalFreeCarpet + totalTerraceGarden;
    const balanceCarpetArea = Math.max(0, totalCarpetArea - existingCarpetArea - totalFreeArea);
    
    // Free area given to society members (in sft, convert to sq mtr)
    const freeAreaToSocietySqft = parseFloat(formData.freeAreaToSociety) || 0;
    const freeAreaToSocietySqMtr = freeAreaToSocietySqft * conversionFactor;
    
    // Saleable area (with loading) - convert to sft for cost calculation
    // Deduct free area given to society from saleable area
    const saleableAreaSqMtr = Math.max(0, (balanceCarpetArea - freeAreaToSocietySqMtr) * (1 + loadingPct / 100));
    const saleableAreaSqft = saleableAreaSqMtr / 0.092903;

    // 1. Marketing Costs - respect mode from Quick Input
    const marketingExpenseMode = formData.marketingExpenseMode || "percent";
    const marketingExpensePercent = parseFloat(formData.marketingExpensePercent) || 3;
    let totalMarketingCost = 0;
    
    // Weighted sale rate for % calculation
    const saleRateFlats = parseFloat(formData.saleRateFlats) || parseFloat(formData.saleRatePerSqft) || 0;
    const weightedSaleRate = saleRateFlats; // Simplified - uses flat rate as primary
    
    if (marketingExpenseMode === "per_sft") {
      const marketingCostPerSqft = parseFloat(formData.marketingCostPerSqft) || 0;
      totalMarketingCost = saleableAreaSqft * marketingCostPerSqft;
    } else {
      // Percentage mode (default) - % of (Sale Area × Sale Rate)
      totalMarketingCost = (saleableAreaSqft * weightedSaleRate) * (marketingExpensePercent / 100);
    }

    // 2. Miscellaneous Expenses
    const miscExpPerSqft = parseFloat(formData.miscExpPerSqft) || 0;
    const totalMiscExp = saleableAreaSqft * miscExpPerSqft;

    // 3. Brokerage & Commission
    let totalBrokerage = 0;
    if (formData.brokerageCommissionType === "fixed") {
      totalBrokerage = parseFloat(formData.brokerageCommissionFixed) || 0;
    } else {
      const brokeragePerSqft = parseFloat(formData.brokerageCommissionPerSqft) || 0;
      totalBrokerage = saleableAreaSqft * brokeragePerSqft;
    }

    // Total Other Costs
    const totalOtherCosts = totalMarketingCost + totalMiscExp + totalBrokerage;

    setCostBreakdown({
      saleableAreaSqft,
      saleableAreaSqMtr,
      freeAreaToSocietySqft,
      freeAreaToSocietySqMtr,
      totalMarketingCost,
      totalMiscExp,
      totalBrokerage,
      totalOtherCosts,
      marketingExpenseMode,
      marketingExpensePercent,
      weightedSaleRate
    });

    // Pass to parent component
    if (setOtherCostsData) {
      setOtherCostsData({
        saleableAreaSqft,
        saleableAreaSqMtr,
        freeAreaToSocietySqft,
        freeAreaToSocietySqMtr,
        totalMarketingCost,
        totalMiscExp,
        totalBrokerage,
        totalOtherCosts,
        marketingExpenseMode,
        marketingExpensePercent,
        weightedSaleRate
      });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <Card className="rounded-sm shadow-sm border-blue-200 bg-blue-50/50">
        <CardContent className="py-4">
          <div className="flex items-center gap-2 text-blue-700">
            <Lock className="h-4 w-4" />
            <p className="text-sm">
              <span className="font-medium">Values are locked.</span> All values on this page are derived from Quick Input sheet and cannot be edited here.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Marketing Costs */}
      <Card className="rounded-sm shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading tracking-tight flex items-center gap-2">
            Marketing Costs
            <Lock className="h-4 w-4 text-muted-foreground" />
          </CardTitle>
          <CardDescription>
            {(formData.marketingExpenseMode || "percent") === "percent" 
              ? "Percentage of (Saleable Area × Sale Rate)" 
              : "Cost per sq ft of saleable area"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(formData.marketingExpenseMode || "percent") === "percent" ? (
            <div className="space-y-2">
              <Label>Marketing Expense (%)</Label>
              <Input
                name="marketingExpensePercent"
                type="number"
                value={formData.marketingExpensePercent || "3"}
                readOnly
                disabled
                className="rounded-sm max-w-xs bg-secondary/50 cursor-not-allowed"
                data-testid="marketing-percent-input"
              />
              <p className="text-xs text-muted-foreground">
                Total = Saleable Area (sft) × Sale Rate × {formData.marketingExpensePercent || 3}%
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="marketingCostPerSqft">Marketing Cost (₹ per sft)</Label>
              <Input
                id="marketingCostPerSqft"
                name="marketingCostPerSqft"
                type="number"
                value={formData.marketingCostPerSqft}
                readOnly
                disabled
                className="rounded-sm max-w-xs bg-secondary/50 cursor-not-allowed"
                data-testid="marketing-cost-input"
              />
              <p className="text-xs text-muted-foreground">
                Total = Saleable Area (sft) × Rate per sft
              </p>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            Mode set in Quick Input: <span className="font-semibold">{(formData.marketingExpenseMode || "percent") === "percent" ? "Percentage" : "Per Sq Ft"}</span>
          </p>
        </CardContent>
      </Card>

      {/* Miscellaneous Expenses */}
      <Card className="rounded-sm shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading tracking-tight flex items-center gap-2">
            Miscellaneous Expenses
            <Lock className="h-4 w-4 text-muted-foreground" />
          </CardTitle>
          <CardDescription>Other expenses per sq ft of saleable area</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="miscExpPerSqft">Misc Expenses (₹ per sft)</Label>
            <Input
              id="miscExpPerSqft"
              name="miscExpPerSqft"
              type="number"
              value={formData.miscExpPerSqft}
              readOnly
              disabled
              className="rounded-sm max-w-xs bg-secondary/50 cursor-not-allowed"
              data-testid="misc-exp-input"
            />
            <p className="text-xs text-muted-foreground">
              Total = Saleable Area (sft) × Rate per sft
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Free Area Given to Society Members - EDITABLE */}
      <Card className="rounded-sm shadow-sm border-green-200">
        <CardHeader>
          <CardTitle className="font-heading tracking-tight">Free Area Given to Society Members</CardTitle>
          <CardDescription>Area given unofficially free of cost beyond development agreement. This will be reduced from total saleable area.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="freeAreaToSociety">Free Area to Society (sft)</Label>
            <Input
              id="freeAreaToSociety"
              name="freeAreaToSociety"
              type="number"
              value={formData.freeAreaToSociety || ""}
              onChange={(e) => setFormData({...formData, freeAreaToSociety: e.target.value})}
              placeholder="0"
              className="rounded-sm max-w-xs"
              data-testid="free-area-society-input"
            />
            <p className="text-xs text-muted-foreground">
              Enter carpet area in sq ft. This will reduce saleable area and affect revenue in P&L sheet.
            </p>
            {parseFloat(formData.freeAreaToSociety) > 0 && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-sm">
                <p className="text-sm text-green-700">
                  <span className="font-medium">{parseFloat(formData.freeAreaToSociety).toLocaleString('en-IN')} sft</span> will be deducted from saleable area
                  <span className="text-xs ml-1">({(parseFloat(formData.freeAreaToSociety) * 0.092903).toFixed(2)} sq mtr)</span>
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Brokerage & Commission */}
      <Card className="rounded-sm shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading tracking-tight flex items-center gap-2">
            Brokerage & Commission
            <Lock className="h-4 w-4 text-muted-foreground" />
          </CardTitle>
          <CardDescription>
            Method: <span className="font-semibold">{formData.brokerageCommissionType === "per_sqft" ? "Per Sq Ft" : "Fixed Amount"}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {formData.brokerageCommissionType === "per_sqft" ? (
            <div className="space-y-2 border-l-2 border-accent pl-4">
              <Label htmlFor="brokerageCommissionPerSqft">Brokerage Rate (₹ per sft)</Label>
              <Input
                id="brokerageCommissionPerSqft"
                name="brokerageCommissionPerSqft"
                type="number"
                value={formData.brokerageCommissionPerSqft}
                readOnly
                disabled
                className="rounded-sm max-w-xs bg-secondary/50 cursor-not-allowed"
                data-testid="brokerage-per-sqft-input"
              />
              <p className="text-xs text-muted-foreground">Total = Saleable Area (sft) × Rate per sft</p>
            </div>
          ) : (
            <div className="space-y-2 border-l-2 border-accent pl-4">
              <Label htmlFor="brokerageCommissionFixed">Fixed Brokerage Amount (₹)</Label>
              <Input
                id="brokerageCommissionFixed"
                name="brokerageCommissionFixed"
                type="number"
                value={formData.brokerageCommissionFixed}
                readOnly
                disabled
                className="rounded-sm max-w-xs bg-secondary/50 cursor-not-allowed"
                data-testid="brokerage-fixed-input"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cost Breakdown Display */}
      {costBreakdown && fsiBreakdown && (
        <Card className="rounded-sm shadow-sm border-accent/50" data-testid="other-costs-breakdown">
          <CardHeader>
            <CardTitle className="font-heading tracking-tight text-accent">Other Costs Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-secondary/50 p-4 rounded-sm">
              <h4 className="font-semibold text-sm mb-2">Saleable Area Reference</h4>
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground">Saleable Area (sq mtr)</p>
                  <p className="font-mono font-medium">{costBreakdown.saleableAreaSqMtr.toFixed(2)} sq mtr</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Saleable Area (sft)</p>
                  <p className="font-mono font-medium">{costBreakdown.saleableAreaSqft.toFixed(2)} sft</p>
                </div>
              </div>
              {costBreakdown.freeAreaToSocietySqft > 0 && (
                <div className="mt-3 pt-3 border-t border-secondary">
                  <p className="text-xs text-green-700">
                    <span className="font-medium">Note:</span> Free area to society ({costBreakdown.freeAreaToSocietySqft.toLocaleString('en-IN')} sft) has been deducted from saleable area
                  </p>
                </div>
              )}
            </div>

            <Separator />

            <div className="space-y-3">
              <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Cost Breakdown</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Marketing Costs 
                    {costBreakdown.marketingExpenseMode === "percent" 
                      ? ` (${costBreakdown.marketingExpensePercent}% of ₹${(costBreakdown.saleableAreaSqft * costBreakdown.weightedSaleRate / 10000000).toFixed(2)} Cr)`
                      : ` (${formData.marketingCostPerSqft || 0} × ${costBreakdown.saleableAreaSqft.toFixed(0)} sft)`}
                  </span>
                  <span className="font-mono font-medium">{formatCurrency(costBreakdown.totalMarketingCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Miscellaneous Expenses ({formData.miscExpPerSqft || 0} × {costBreakdown.saleableAreaSqft.toFixed(0)} sft)</span>
                  <span className="font-mono font-medium">{formatCurrency(costBreakdown.totalMiscExp)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Brokerage & Commission 
                    {formData.brokerageCommissionType === "per_sqft" 
                      ? ` (${formData.brokerageCommissionPerSqft || 0} × ${costBreakdown.saleableAreaSqft.toFixed(0)} sft)` 
                      : " (Fixed)"}
                  </span>
                  <span className="font-mono font-medium">{formatCurrency(costBreakdown.totalBrokerage)}</span>
                </div>
              </div>
            </div>

            <Separator />

            <div className="bg-accent/10 p-4 rounded-sm border border-accent/30">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-lg">Total Other Costs</span>
                <span className="font-mono font-bold text-2xl text-accent">{formatCurrency(costBreakdown.totalOtherCosts)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {!fsiBreakdown && (
        <Card className="rounded-sm shadow-sm bg-secondary/50">
          <CardContent className="py-8 text-center text-muted-foreground">
            <p>Please complete FSI Calculator and Revenue tabs first to view cost breakdown</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default OtherCosts;
